/** Known feature flags. Add a string here to register a new flag. */
export type FeatureFlag = 'ai' | 'apps' | 'tax'

/** Every registered flag, used to iterate when resolving state. */
export const ALL_FEATURE_FLAGS: readonly FeatureFlag[] = [
  'ai',
  'apps',
  'tax',
]

/**
 * Keycloak groups whose name starts with this prefix enable the matching
 * feature flag. e.g. membership of the `feature-ai` group enables the `ai`
 * flag. This keeps flag rollout self-service in Keycloak: create a group,
 * add the users who should see the feature, done.
 */
export const GROUP_FLAG_PREFIX = 'feature-'

/** localStorage key holding developer flag overrides (see RFC-0021). */
const STORAGE_KEY = 'feature_flags'

/** Where a resolved flag value came from, for display/debugging. */
export type FeatureFlagSource = 'group' | 'localStorage' | 'off'

/** Reads the developer-controlled flag overrides from localStorage. */
export function getLocalStorageFlags(): Partial<
  Record<FeatureFlag, boolean>
> {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}')
  } catch {
    return {}
  }
}

/**
 * Derives the set of enabled flags from a user's Keycloak groups.
 *
 * Group names may arrive either bare (`feature-ai`) or as a full Keycloak
 * path (`/parent/feature-ai`); both are handled by taking the last path
 * segment. Only segments after {@link GROUP_FLAG_PREFIX} that match a known
 * {@link FeatureFlag} are honoured — any other group is ignored.
 */
export function flagsFromGroups(
  groups: readonly string[] | undefined
): Set<FeatureFlag> {
  const enabled = new Set<FeatureFlag>()
  for (const raw of groups ?? []) {
    const name = raw.split('/').pop() ?? raw
    if (!name.startsWith(GROUP_FLAG_PREFIX)) continue
    const candidate = name.slice(GROUP_FLAG_PREFIX.length)
    if (
      (ALL_FEATURE_FLAGS as readonly string[]).includes(
        candidate
      )
    ) {
      enabled.add(candidate as FeatureFlag)
    }
  }
  return enabled
}

export interface ResolvedFeatureFlags {
  /** Final on/off value per flag. */
  flags: Record<FeatureFlag, boolean>
  /** Why each flag resolved the way it did. */
  sources: Record<FeatureFlag, FeatureFlagSource>
  /** Raw Keycloak groups the user belongs to (for display/debugging). */
  groups: string[]
}

/**
 * Resolves the effective flag state by combining Keycloak group membership
 * (the source of truth, driven from the JWT) with developer localStorage
 * overrides (handy for local testing). A flag is on if EITHER source enables
 * it; group membership wins as the reported source.
 */
export function resolveFeatureFlags(
  groups: readonly string[] | undefined
): ResolvedFeatureFlags {
  const groupFlags = flagsFromGroups(groups)
  const localFlags = getLocalStorageFlags()

  const flags = {} as Record<FeatureFlag, boolean>
  const sources = {} as Record<FeatureFlag, FeatureFlagSource>

  for (const flag of ALL_FEATURE_FLAGS) {
    if (groupFlags.has(flag)) {
      flags[flag] = true
      sources[flag] = 'group'
    } else if (localFlags[flag] === true) {
      flags[flag] = true
      sources[flag] = 'localStorage'
    } else {
      flags[flag] = false
      sources[flag] = 'off'
    }
  }

  return { flags, sources, groups: [...(groups ?? [])] }
}
